import type { FileFormatScope } from '@steerfi/shared';
import { IconSearch } from '@tabler/icons-react';
import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { getClients } from '@/api/clients';
import { listProviders } from '@/api/providers';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

interface PartyOption {
  partyId: string;
  displayName: string;
  code: string;
}

type PartyScope = Extract<FileFormatScope, 'provider' | 'client'>;
type FetchPartyOptions = (search: string) => Promise<PartyOption[]>;

interface PartySearchPickerProps {
  scope: PartyScope;
  selectedPartyId: string | null;
  onSelect: (partyId: string) => void;
  error?: string | undefined;
}

const PAGE_SIZE = 50;

// Strategy table: scope -> "how do I fetch and normalize options for this
// scope?". Adding a new party-scoped role means appending one row;
// fetchOptions itself never branches on scope. The two providers and
// clients APIs return different shapes, so each strategy owns the
// adaptation into the common PartyOption row used by the dropdown.
const PARTY_STRATEGIES: Record<PartyScope, FetchPartyOptions> = {
  provider: async (search) => {
    const result = await listProviders({
      page: 1,
      pageSize: PAGE_SIZE,
      status: 'active',
      sortBy: 'provider_name',
      order: 'asc',
      ...(search ? { search } : {}),
    });
    return result.data.data.map((p) => ({
      partyId: p.party_id,
      displayName: p.provider_name,
      code: p.provider_code,
    }));
  },
  client: async (search) => {
    const result = await getClients({
      page: 1,
      limit: PAGE_SIZE,
      sortBy: 'name',
      order: 'asc',
      ...(search ? { search } : {}),
    });
    return result.data.map((c) => ({
      partyId: c.id,
      displayName: c.name,
      code: c.client_code ?? '',
    }));
  },
};

export const PartySearchPicker: React.FC<PartySearchPickerProps> = ({
  scope,
  selectedPartyId,
  onSelect,
  error,
}) => {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [options, setOptions] = useState<PartyOption[]>([]);
  // Start in the loading state so the very first paint shows skeletons
  // instead of briefly flashing "No results found" before `fetchOptions`
  // gets a chance to run inside the effect below.
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchOptions = useCallback(async () => {
    setIsLoading(true);
    try {
      setOptions(await PARTY_STRATEGIES[scope](debouncedSearch));
    } catch {
      setOptions([]);
    } finally {
      setIsLoading(false);
    }
  }, [scope, debouncedSearch]);

  useEffect(() => {
    fetchOptions();
  }, [fetchOptions]);

  const label = scope === 'provider' ? 'Select provider' : 'Select client';
  const placeholder = scope === 'provider' ? 'Search providers...' : 'Search clients...';

  // Five rows roughly fills the fixed h-48 (192px) options container at the
  // current row height (~38px). Mirrors the real row layout (display name
  // on the left, code badge on the right) so there's no layout shift when
  // results swap in.
  const skeletonRows = Array.from({ length: 5 }, (_, i) => (
    <div
      // biome-ignore lint/suspicious/noArrayIndexKey: stable placeholder rows
      key={i}
      className="flex items-center justify-between border-b px-3 py-2.5 last:border-b-0"
      aria-hidden
    >
      <Skeleton className="h-4 w-40" />
      <Skeleton className="h-3 w-10" />
    </div>
  ));

  return (
    <div className="flex flex-col gap-2">
      <Label className="font-semibold">{label}</Label>
      <div className="rounded-md border">
        <div className="relative border-b px-3 py-2">
          <IconSearch className="absolute left-5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="border-0 pl-9 shadow-none"
            placeholder={placeholder}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="h-48 overflow-y-auto" aria-busy={isLoading} aria-live="polite">
          {isLoading ? (
            skeletonRows
          ) : options.length === 0 ? (
            <p className="px-3 py-4 text-sm text-muted-foreground">No results found</p>
          ) : (
            options.map((option) => (
              <button
                key={option.partyId}
                type="button"
                className={cn(
                  'flex w-full items-center justify-between border-b px-3 py-2.5 text-left text-sm last:border-b-0 hover:bg-muted/50',
                  selectedPartyId === option.partyId && 'bg-muted inset-ring inset-ring-foreground'
                )}
                aria-pressed={selectedPartyId === option.partyId}
                onClick={() => onSelect(option.partyId)}
              >
                <span>{option.displayName}</span>
                {option.code ? (
                  <span className="text-xs uppercase text-muted-foreground">{option.code}</span>
                ) : null}
              </button>
            ))
          )}
        </div>
      </div>
      {error ? <p className="text-sm text-destructive">{error}</p> : null}
    </div>
  );
};
